import * as React from 'react'
import {
  CompactSelection,
  CustomRenderer,
  DataEditor,
  DataEditorRef,
  EditListItem,
  EditableGridCell,
  GridCellKind,
  GridColumn,
  GridKeyEventArgs,
  GridMouseEventArgs,
  GridSelection,
  Item,
  Theme,
} from '@basehub/glide-data-grid'
import { FullBleedContainer } from '../../../../../../_components/full-bleed-container'
import {
  HEADER_HEIGHT,
  HEADER_HEIGHT_WITH_BORDERS,
  HEADER_ICONS,
  LIST_BLOCK_DARK_THEME,
  LIST_BLOCK_LIGHT_THEME,
  ROW_HEIGHT,
} from './theme'
import {
  AtLeast,
  Block,
  BlockChange,
  BlockChangeChildStatus,
  CommonBlocksCompoundIndexes,
  InstanceBlock,
  ListBlock,
  ListView,
  MediaBlock,
  MediaBlockValueType,
  blockTypesRecord,
  getBlockUpdateOverride,
  getTitleUpdateOverride,
  isCommentBlock,
} from '@basehub/data/replicache/index'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import {
  ListBlockRow,
  LoadMoreItemsButton,
  ShowOnFirstLoadMenu,
  UseListTableResult,
  useListTable,
} from '../../use-list-table'
import { useGetCellContent } from './use-get-cell-content'
import { BaseHubCollectionColumn } from './types'
import { getId } from '@basehub/utils/id/index'
import { AddColumnMenu, RightColumn } from '../../input-ui/add-column'
import { useSelectedView } from '../use-selected-view'
import { Button } from '~/sotano-kit/primitives/button'
import { ArrowDownIcon, PlusIcon, TrashIcon } from '@radix-ui/react-icons'
import { invariant } from '@epic-web/invariant'
import { ConfirmSortDeletionModal } from './sort'
import { clsx } from 'clsx'
import {
  MultiSelectRenderer,
  multiSelectCellSchema,
} from './custom-cells/selector'
import {
  DatePickerRenderer,
  datePickerCellSchema,
} from './custom-cells/date-picker'
import {
  ColorPickerRenderer,
  colorPickerCellSchema,
} from './custom-cells/color-picker'
import {
  MediaPickerRenderer,
  mediaPickerCellSchema,
} from './custom-cells/assets/media-picker'
import {
  BooleanCellRenderer,
  booleanCellSchema,
} from './custom-cells/boolean-cell'
import { MediaPreviewRenderer } from './custom-cells/assets/media-preview'
import { uploadFileToRepo } from '~/lib/file-utils'
import { getFileHashSHA256 } from '@basehub/data/blob/file-utils'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { isValidTypeForCategory } from '@basehub/data/replicache/schema/block/valid-mime-types'
import { SpinnerCellRenderer } from './custom-cells/spinner-cell'
import { BlockCellRenderer, blockCellSchema } from './custom-cells/block-cell'
import { TreeManager } from '@basehub/utils/tree/index'
import {
  useEditorMainContentHeight,
  useEditorWidth,
} from '~/hooks/use-editor-width'
import { getBlockFromColumnId } from '@basehub/data/replicache/schema/block/list/utils'
import { DroppableBottomZone, DroppableTopZone } from './dnd/droppable-zones'
import {
  DroppableProvider,
  listBlockDndControllers,
  useListDndTreeNode,
} from '../dnd'
import debounce from 'lodash.debounce'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { mergeRefs } from '~/lib/merge-refs'
import { useEditorStore } from '../../../../editor-store'
import { useRepoStore } from '../../../../repo-store'
import { ViewComponentProps } from '../../input'
import {
  tabContentId,
  tabContentWrapperId,
} from '../../../../../../tab-content/wrapper'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { useMutateWithErrorHandling } from '~/replicache/frontend/use-mutate-with-error-handling'
import { useTheme } from 'next-themes'
import { TableContextMenu } from './context-menu'
import { getOffsetTop } from '~/lib/dom-utils'
import { useDndContext } from '@dnd-kit/core'
import {
  RepoUndoOps,
  RepoUndoStackItem,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { SparklineCellRenderer } from './custom-cells/sparkline-cell'
import {
  CreateBlockParams,
  DeleteBlocksParams,
  UpdateBlockParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { UpdateListViewParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/list'
import { BlockBatchMutationParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/batch'
import { run } from '@basehub/utils'
import { EditorMode, useEditorMode } from '../../../../editor-mode-provider'
import { useGetTabPrefix, useTabsStore } from '../../../../tabs/store'
import { SvgCellRenderer, svgCellSchema } from './custom-cells/svg-cell'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { getSafeMediaTypeByConstraints } from '../../../media/utils'
import { toast } from 'sonner'
import { Spinner } from '~/components/misc/spinner'

const RowActions = React.lazy(() => import('./dnd/row-actions'))

export const RENDERERS = [
  MultiSelectRenderer,
  DatePickerRenderer,
  ColorPickerRenderer,
  MediaPickerRenderer,
  MediaPreviewRenderer,
  SvgCellRenderer,
  BooleanCellRenderer,
  SpinnerCellRenderer,
  BlockCellRenderer,
  SparklineCellRenderer,
]

export type TableProps = {
  block: ListBlock
  path: string
  emptyState: React.ReactNode
  onKeyDown?: (
    e: GridKeyEventArgs,
    options: { currentColumn: BaseHubCollectionColumn }
  ) => void
  editorMode?: EditorMode
  fullView: boolean | undefined
  justCreatedTableRowId?: Block['id'] | null
  onFinishedCreatingRow?: (
    newRow: ListBlockRow | null,
    opts: { isVisible: boolean }
  ) => void
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
  treeManagerRef: React.RefObject<null | TreeManager<
    Block,
    CommonBlocksCompoundIndexes
  >>
  rowActions?: boolean
  handleNewRow?: React.ComponentProps<typeof DataEditor>['onRowAppended']
  getRowThemeOverride?: (row: ListBlockRow) => Partial<Theme> | undefined
  tabPrefix: string
  isDiff?: boolean
  stickyWrapperRef?: React.RefObject<null | HTMLDivElement>
  bleedContainerRef?: React.RefObject<null | HTMLDivElement>
  listTable: UseListTableResult
}

export const Table = React.forwardRef<DataEditorRef, TableProps>(
  (
    {
      block,
      path,
      emptyState,
      onKeyDown,
      editorMode,
      fullView,
      justCreatedTableRowId,
      onFinishedCreatingRow,
      treeManager,
      treeManagerRef,
      rowActions = true,
      handleNewRow,
      getRowThemeOverride,
      tabPrefix,
      isDiff = false,
      stickyWrapperRef,
      bleedContainerRef,
      listTable,
    },
    ref
  ) => {
    const editorHeight = useEditorMainContentHeight()
    const { data } = useRepoReplicache()
    const repoId = data?.repoId
    const teamId = data?.team.id
    const [gridSelection, setGridSelection] = React.useState<GridSelection>({
      columns: CompactSelection.empty(),
      rows: CompactSelection.empty(),
    })
    const justDroppedTableRowsId = React.useRef<Block['id'][]>([])
    const selectedDashboardVariants = useSelectedVariants({
      parentBlock: block,
    })
    const tableContainerRef = React.useRef<HTMLDivElement>(null)
    const dataEditorRef = React.useRef<DataEditorRef>(null)
    const replicache = data?.replicache
    const [
      sortRemovalConfirmationModalOpened,
      setSortRemovalConfirmationModalOpened,
    ] = React.useState(false)
    const [selectedView, updateSelectedView] = useSelectedView(block)
    const undoManager = useRepoUndoManager()
    const activeTabKey = useTabsStore((s) => s.activeTab?.key)
    const sourceBlockId = idToSourceBlockId(block)
    const focusOpTable = React.useMemo(() => {
      return {
        op: 'focus',
        args: {
          focus: {
            type: 'block:wrapper',
            blockId: sourceBlockId,
          },
          preferredTabKey: activeTabKey,
        },
      } satisfies RepoUndoOps[number]
    }, [sourceBlockId, activeTabKey])

    const theme =
      useTheme().resolvedTheme === 'dark'
        ? LIST_BLOCK_DARK_THEME
        : LIST_BLOCK_LIGHT_THEME
    const rightColumnRef = React.useRef<HTMLDivElement>(null)
    const [_stickyWrapperParentOffsetTop, setStickyWrapperParentOffsetTop] =
      React.useState<number>()
    const [scrollerRefWasSet, setScrollerRefWasSet] =
      React.useState<HTMLDivElement | null>(null)
    const scrollerRef = React.useRef<HTMLDivElement | null>(null)
    const readonly = editorMode === 'read' || isDiff
    const {
      rows,
      columns,
      listTemplate,
      rowsShown,
      loadMore,
      length,
      allRowsShown,
      isLoading,
    } = listTable

    const baseTableHeight = React.useMemo(() => {
      return (
        Math.max(
          HEADER_HEIGHT_WITH_BORDERS + rows.length * ROW_HEIGHT,
          HEADER_HEIGHT_WITH_BORDERS * 2
        ) - (readonly ? ROW_HEIGHT : 0)
      )
    }, [rows.length, readonly])

    const getActivateScrollVirtualizer = React.useCallback(
      (tableHeight: number) => {
        return Boolean(fullView && tableHeight > (editorHeight ?? 1))
      },
      [editorHeight, fullView]
    )

    const extraRowCountForVirtualization = React.useMemo(() => {
      let extraRowCount = 0
      if (!getActivateScrollVirtualizer(baseTableHeight) && scrollerRefWasSet) {
        return 0
      }
      if (!allRowsShown) extraRowCount++
      if (!readonly && !!handleNewRow) extraRowCount++
      return extraRowCount
    }, [
      allRowsShown,
      baseTableHeight,
      getActivateScrollVirtualizer,
      handleNewRow,
      readonly,
      scrollerRefWasSet,
    ])

    const tableHeight = React.useMemo(() => {
      const activateScrollVirtualizer =
        getActivateScrollVirtualizer(baseTableHeight)

      if (!activateScrollVirtualizer || !scrollerRefWasSet) {
        return baseTableHeight + ROW_HEIGHT
      }

      return baseTableHeight + extraRowCountForVirtualization * ROW_HEIGHT
    }, [
      getActivateScrollVirtualizer,
      baseTableHeight,
      scrollerRefWasSet,
      extraRowCountForVirtualization,
    ])

    const [_, setFillerHeight] = React.useState<number>()
    const activateScrollVirtualizer =
      fullView &&
      scrollerRefWasSet &&
      tableHeight > (scrollerRef.current?.offsetHeight ?? 1)
    // offsets for scroll
    const stickyWrapperParentOffsetTop =
      !activateScrollVirtualizer || !scrollerRefWasSet
        ? Infinity
        : (_stickyWrapperParentOffsetTop ?? 0)
    let stickyWrapperParentOffsetBottom = 0
    if (
      activateScrollVirtualizer &&
      scrollerRefWasSet &&
      scrollerRef.current?.scrollHeight
    ) {
      stickyWrapperParentOffsetBottom =
        scrollerRef.current?.scrollHeight -
        tableHeight -
        stickyWrapperParentOffsetTop
      stickyWrapperParentOffsetBottom +=
        HEADER_HEIGHT_WITH_BORDERS * extraRowCountForVirtualization
    }

    const rootId = treeManager?.root.key

    useListDndTreeNode({
      rootId: rootId ?? '',
      block,
      enabled: Boolean(rootId && block.id && !readonly),
      treeManager,
      undo: {
        manager: undoManager.current,
        focusOp: focusOpTable,
      },
      onDropCallback: (newIndex, keysDragged) => {
        // since the newIndex comes from the state before the drop,
        // we need to modify it based on the rows that won't be there when the drop happens
        let modifier = 0
        const itemsBefore = rows.slice(0, newIndex)
        for (const key of keysDragged) {
          if (
            itemsBefore.some(
              (item) => item.id === key.id || idToSourceBlockId(item) === key.id
            )
          ) {
            modifier++
          }
        }

        setGridSelection({
          rows: CompactSelection.fromSingleSelection([
            newIndex - modifier,
            Array.from(keysDragged).length + newIndex - modifier,
          ]),
          columns: CompactSelection.empty(),
        })
      },
    })

    const getCellContent = useGetCellContent({
      rows,
      block,
      template: listTemplate,
      treeManagerRef,
      editorMode,
      tabPrefix,
    })

    const templateBlockPath = React.useMemo(() => {
      if (!listTemplate || !treeManager) return null

      return treeManager.getPath(listTemplate.id)
    }, [listTemplate, treeManager])

    const disableColumnAdd =
      !templateBlockPath || editorMode !== 'write:all' || isDiff || readonly

    const highlights = React.useMemo(() => {
      if (!gridSelection.columns) return
      const [selectingColumnIndex] = gridSelection.columns.toArray()
      if (!selectingColumnIndex) return

      return [
        {
          color: theme.accentLight,
          style: 'no-outline' as const,
          range: {
            x: selectingColumnIndex,
            y: 0,
            width: 1,
            height: rows.length,
          },
        },
      ]
    }, [gridSelection.columns, rows.length, theme.accentLight])

    const handleColumnMoved = React.useCallback(
      async (from: number, to: number) => {
        if (!treeManager || !replicache?.mutate) return

        let newColumnsMetadata: ListView['columnsMetadata'] = []
        if (!!selectedView?.columnsMetadata) {
          const columnIdSet = new Set(
            selectedView?.columnsMetadata.map((cm) => cm.blockId)
          )
          newColumnsMetadata = [...selectedView.columnsMetadata]

          columns.forEach((column) => {
            if (columnIdSet.has(column.id)) return
            newColumnsMetadata.push({
              blockId: column.id,
              width: column.width,
              hidden: false,
            })
          })
        } else {
          newColumnsMetadata = [
            {
              blockId: '__title',
              hidden: false,
              width: 200,
            },
            ...(
              (listTemplate &&
                treeManager.getChildrenKeys(idToSourceBlockId(listTemplate))) ||
              []
            )
              .map((childId) => {
                const c = treeManager.getNode(childId, {
                  required: true,
                }).value
                if (!c || isCommentBlock(c)) return

                return { blockId: childId, hidden: false, width: 200 }
              })
              .filter(Boolean),
          ]
        }

        // Get visible columns and find their actual indices
        const visibleColumns = newColumnsMetadata.filter((col) => !col.hidden)
        const fromBlockId = visibleColumns[from]?.blockId
        const toBlockId = visibleColumns[to]?.blockId

        if (!fromBlockId || !toBlockId) return

        const fromIndex = newColumnsMetadata.findIndex(
          (col) => col.blockId === fromBlockId
        )
        const toIndex = newColumnsMetadata.findIndex(
          (col) => col.blockId === toBlockId
        )

        // Move column in the full metadata array
        const [removed] = newColumnsMetadata.splice(fromIndex, 1)
        if (removed) newColumnsMetadata.splice(toIndex, 0, removed)

        const listIdInTemplate = block.instanceChildTarget ?? block.id
        if (!listIdInTemplate) return

        const params: UpdateListViewParams = {
          path: treeManager.getPath(listIdInTemplate, { required: true }),
          id: selectedView?.id,
          blockId: listIdInTemplate,
          idSeed: getId(),
          targetBlockId_withinTree: treeManager.getNode(
            block.typeOptions.targetBlockId,
            { required: true }
          ).value.id,
          data: { columnsMetadata: newColumnsMetadata },
          isoNow: new Date().toISOString(),
          forceComponentInstancesSync: listIdInTemplate !== block.id,
        }
        const { view, undo } = await replicache.mutate.updateListView(params)

        undoManager.current?.push({
          redo: [focusOpTable, { op: 'updateListView', args: params }],
          undo: [
            focusOpTable,
            { op: 'blockBatchMutations', args: { mutations: undo } },
          ],
        })

        if (view && selectedView?.id !== view.id) {
          await updateSelectedView(view.id)
        }
      },
      [
        block.id,
        block.typeOptions.targetBlockId,
        block.instanceChildTarget,
        listTemplate,
        columns,
        focusOpTable,
        replicache?.mutate,
        selectedView?.columnsMetadata,
        selectedView?.id,
        treeManager,
        undoManager,
        updateSelectedView,
      ]
    )

    React.useEffect(() => {
      if (!justCreatedTableRowId || !treeManager?.getNode) return

      const justCreatedRowNode = treeManager.getNode(justCreatedTableRowId)
      if (!justCreatedRowNode) return

      const newRowIsChildOfList = justCreatedRowNode.parentKey === block.id
      if (!newRowIsChildOfList) return

      const newRowIndex = rows.findIndex(
        (row) => row.id === justCreatedTableRowId
      )
      const newRow =
        rows[newRowIndex] ?? (justCreatedRowNode.value as ListBlockRow)

      const isVisible = newRowIndex !== -1

      // the new row is listed in the table, we do not open it in the full view
      if (!isVisible) {
        // the new row is not listed (because of filters / pagination), we open it in the full view by telling that it !isVisible
        onFinishedCreatingRow?.(newRow, {
          isVisible: false,
        })
      }
    }, [
      block.id,
      columns,
      justCreatedTableRowId,
      onFinishedCreatingRow,
      rows,
      treeManager,
    ])

    const recentlyCreatedRows = React.useMemo((): {
      creationMode: 'created' | 'dropped'
      newRowsIndexes: number[]
    } | null => {
      let creationMode: 'created' | 'dropped' = 'created'
      let newRowsIds: string[] = []
      if (justCreatedTableRowId) {
        creationMode = 'created'
        newRowsIds = [justCreatedTableRowId]
      } else if (justDroppedTableRowsId.current.length) {
        creationMode = 'dropped'
        newRowsIds = justDroppedTableRowsId.current
      } else {
        return null
      }

      return {
        creationMode,
        newRowsIndexes: newRowsIds
          .map((newRowId) => {
            const rowIndex = rows.findIndex((row) => row.id === newRowId)
            return rowIndex
          })
          .filter((idx) => idx !== -1),
      }
    }, [justCreatedTableRowId, rows])

    const updateJustCreateadTableRowsId = React.useCallback(
      (newRowIds: typeof justDroppedTableRowsId.current) => {
        justDroppedTableRowsId.current = newRowIds
      },
      []
    )

    // to follow the newly created row when sorts are applied
    React.useEffect(() => {
      if (!recentlyCreatedRows || !recentlyCreatedRows.newRowsIndexes.length) {
        return
      }

      setGridSelection((prevGridSelection) => {
        if (
          recentlyCreatedRows.newRowsIndexes.length === 1 &&
          prevGridSelection.current?.cell[1] ===
            recentlyCreatedRows.newRowsIndexes?.[0]
        ) {
          return prevGridSelection
        }

        // if the creation was due to assets being dropped, highlight the entire row
        if (recentlyCreatedRows.creationMode === 'dropped') {
          let newRowSelection = CompactSelection.empty()
          recentlyCreatedRows.newRowsIndexes.forEach((idx) => {
            newRowSelection = newRowSelection.add(idx)
          })

          justDroppedTableRowsId.current = []
          onFinishedCreatingRow?.(null, { isVisible: true })
          return {
            columns: CompactSelection.empty(),
            rows: newRowSelection,
          }
          // if not, just highlight the title cell
        } else if (recentlyCreatedRows.newRowsIndexes.length === 1) {
          const columnSelection = prevGridSelection.current?.cell[0] ?? 0
          const indexOfNewRow = recentlyCreatedRows.newRowsIndexes[0]
          if (!indexOfNewRow) return prevGridSelection

          return {
            columns: CompactSelection.empty(),
            rows: CompactSelection.empty(),
            current: {
              cell: [columnSelection, indexOfNewRow],
              range: {
                x: columnSelection,
                y: indexOfNewRow,
                width: 1,
                height: 1,
              },
              rangeStack: [],
            },
          }
        } else {
          return prevGridSelection
        }
      })
    }, [onFinishedCreatingRow, recentlyCreatedRows])

    const { mutateAsync: handleDeleteWithErrorHandling } =
      useMutateWithErrorHandling(
        async (
          mutate,
          {
            columns: columnSelection,
            rows: rowSelection,
            current,
          }: GridSelection
        ) => {
          if (!treeManager || !replicache || readonly) return false

          if (current) {
            const { width, height, x, y } = current.range
            // get touched rows, using starting and height
            const touchedRows = Array.from({ length: height }, (_, i) => y + i)
            const touchedColumns = Array.from(
              { length: width },
              (_, i) => x + i
            )

            const rowsToClearTitle: Array<Block['id']> = []
            const cellsToClear: Block['id'][] = []

            columns.forEach((cm, index) => {
              if (!touchedColumns.includes(index)) return []
              return rows.flatMap((row, index) => {
                if (!touchedRows.includes(index)) return []

                if (cm.id === '__title') rowsToClearTitle.push(row.id)
                else {
                  const { block } = getBlockFromColumnId(cm.id, {
                    row,
                    treeManager,
                  })
                  if (!block) return []
                  cellsToClear.push(block.id)
                }
              })
            })

            const params: BlockBatchMutationParams = {
              mutations: [
                ...rowsToClearTitle.map((rowId) => {
                  const block = treeManager.getNode(rowId, { required: true })
                  return {
                    op: 'updateBlock',
                    args: {
                      instanceChildTarget: block.value.instanceChildTarget,
                      data: { title: '' },
                      id: rowId,
                      strategy: 'merge-deep',
                      isoNow: new Date().toISOString(),
                      path,
                    },
                  } as const
                }),
                {
                  op: 'clearListCells',
                  args: {
                    blockIds: cellsToClear,
                    isoNow: new Date().toISOString(),
                    rootId: treeManager.root.key,
                  },
                },
              ],
            }
            const { undo } = await mutate.blockBatchMutations(params)

            undoManager.current?.push({
              redo: [
                focusOpTable,
                { op: 'blockBatchMutations', args: params },
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { refresh: true },
                  },
                },
              ],
              undo: [
                focusOpTable,
                { op: 'blockBatchMutations', args: { mutations: undo } },
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { refresh: true },
                  },
                },
              ],
            })

            return
          } else if (rowSelection.length) {
            const rowIndexesToDelete = rowSelection.toArray()
            const rowIdsToDelete = rowIndexesToDelete.flatMap((index) => {
              const rowToDelete = rows[index]
              if (!rowToDelete) return []
              return rowToDelete.id
            })

            const params: DeleteBlocksParams = {
              blockIds: rowIdsToDelete,
              idSeed: getId(),
              isoNow: new Date().toISOString(),
              rootId: treeManager.root.key,
            }
            const result = await mutate.deleteBlocks(params)
            setGridSelection({
              columns: CompactSelection.empty(),
              rows: CompactSelection.empty(),
            })

            undoManager.current?.push({
              redo: [
                focusOpTable,
                { op: 'deleteBlocks', args: params },
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { refresh: true },
                  },
                },
              ],
              undo: [
                focusOpTable,
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { refresh: true },
                  },
                },
              ],
            })

            return
          } else if (columnSelection.length) {
            const columnIndexesToDelete = columnSelection.toArray()
            const columnIdsToDelete = columnIndexesToDelete.flatMap((index) => {
              const columnToDelete = columns[index]
              if (!columnToDelete) return []
              return columnToDelete.id
            })

            const params: DeleteBlocksParams = {
              blockIds: columnIdsToDelete,
              idSeed: getId(),
              isoNow: new Date().toISOString(),
              rootId: treeManager.root.key,
            }
            const result = await mutate.deleteBlocks(params)
            setGridSelection({
              columns: CompactSelection.empty(),
              rows: CompactSelection.empty(),
            })

            undoManager.current?.push({
              redo: [
                focusOpTable,
                { op: 'deleteBlocks', args: params },
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { refresh: true },
                  },
                },
              ],
              undo: [
                focusOpTable,
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { refresh: true },
                  },
                },
              ],
            })

            return
          }
        }
      )

    const handleColumnResizeEnd = React.useCallback(
      async (column: GridColumn, size: number) => {
        if (!replicache || !treeManagerRef.current || !column.id) return

        let columnsMetadata: ListView['columnsMetadata'] = []
        if (!!selectedView) {
          let isInColumnsMetadata = false
          columnsMetadata = selectedView.columnsMetadata.map((cm) => {
            if (column.id !== cm.blockId) return cm
            isInColumnsMetadata = true
            return { ...cm, width: size }
          })
          if (!isInColumnsMetadata) {
            columnsMetadata.push({
              blockId: column.id,
              width: size,
              hidden: false,
            })
          }
        } else {
          columnsMetadata = columns.map((_column) => {
            const columnSourceBlockId =
              _column.id === '__title'
                ? '__title'
                : idToSourceBlockId(_column.id, treeManagerRef.current!)

            if (column.id !== columnSourceBlockId) {
              return {
                blockId: columnSourceBlockId,
                width: _column.width,
                hidden: false,
              }
            }

            return {
              blockId: columnSourceBlockId,
              width: size,
              hidden: false,
            }
          })
        }

        const listIdInTemplate = block.instanceChildTarget ?? block.id
        if (!listIdInTemplate) return
        const params: UpdateListViewParams = {
          idSeed: getId(),
          id: selectedView?.id,
          blockId: listIdInTemplate,
          targetBlockId_withinTree:
            treeManagerRef.current.getNode(block.typeOptions.targetBlockId, {
              required: true,
            }).value.id ?? '',
          data: { columnsMetadata },
          isoNow: new Date().toISOString(),
          path: treeManagerRef.current.getPath(listIdInTemplate, {
            required: true,
          }),
          // if the list is under an instance, we need to sync the component instances to prevent the list from being out of sync
          forceComponentInstancesSync: listIdInTemplate !== block.id,
        }
        const { view: editedView, undo } =
          await replicache.mutate.updateListView(params)
        if (!editedView) return
        updateSelectedView(editedView.id)

        undoManager.current?.push({
          redo: [focusOpTable, { op: 'updateListView', args: params }],
          undo: [
            focusOpTable,
            { op: 'blockBatchMutations', args: { mutations: undo } },
          ],
        })
      },
      [
        replicache,
        treeManagerRef,
        selectedView,
        columns,
        block.instanceChildTarget,
        block.typeOptions.targetBlockId,
        block.id,
        updateSelectedView,
        undoManager,
        focusOpTable,
      ]
    )

    React.useEffect(() => {
      const handleFocusChange = debounce(() => {
        // editor portal
        if (!treeManager) return
        const portal = document.querySelector<HTMLDivElement>('div#portal')

        const activeElementIsWithinEditinPortal = portal?.contains(
          document.activeElement
        )
        const activeElementIsWithinTable = tableContainerRef.current?.contains(
          document.activeElement
        )
        const justCreatedRowNode = justCreatedTableRowId
          ? treeManager.getNode(justCreatedTableRowId)
          : null
        const justCreatedRowIsChildOfList = justCreatedRowNode
          ? justCreatedRowNode.parentKey === block.id
          : false
        const isInContextMenu = contextMenuRef.current?.contains(
          document.activeElement
        )

        if (
          activeElementIsWithinTable ||
          activeElementIsWithinEditinPortal ||
          justCreatedRowIsChildOfList ||
          isInContextMenu
        ) {
          return
        }

        setGridSelection({
          columns: CompactSelection.empty(),
          rows: CompactSelection.empty(),
        })
      }, 80)

      window.addEventListener('focus', handleFocusChange, true)
      window.addEventListener('blur', handleFocusChange, true)

      // Clean up event listeners on component unmount
      return () => {
        window.removeEventListener('focus', handleFocusChange, true)
        window.removeEventListener('blur', handleFocusChange, true)
      }
    }, [block.id, justCreatedTableRowId, treeManager])

    const hasRowsSelected = gridSelection.rows.length > 0

    const getRowThemeOverrideWrapper = React.useCallback(
      (rowNumber: number | ListBlockRow) => {
        // if rownumber is not a number, return undefined
        if (!Number.isInteger(rowNumber)) return

        const row = rows[rowNumber as number]
        if (!row) return undefined
        if (!getRowThemeOverride) {
          return {
            textDark: row.hidden ? theme.textDark + '66' : theme.textDark,
          }
        }

        const rowThemeOverride = getRowThemeOverride(row)
        let textDark = rowThemeOverride?.textDark ?? theme.textDark
        if (textDark) {
          textDark = row.hidden ? textDark + '66' : textDark
        }

        return { ...rowThemeOverride, textDark }
      },
      [getRowThemeOverride, rows, theme.textDark]
    )

    React.useEffect(() => {
      if (
        !fullView ||
        !stickyWrapperRef ||
        typeof _stickyWrapperParentOffsetTop === 'undefined'
      ) {
        return
      }
      const scroller = getVerticalScrollerElement()
      if (!scroller) return

      if (!scrollerRefWasSet) {
        setScrollerRefWasSet(scroller)
        scrollerRef.current = scroller
      }

      const wrapper = stickyWrapperRef.current
      const rightColumn = rightColumnRef.current

      if (wrapper) {
        wrapper.style.top = '0px'
        wrapper.style.position = 'sticky'
        if ((editorHeight || Infinity) < tableHeight) {
          wrapper.style.height = `calc(var(--editor-height) + ${ROW_HEIGHT * extraRowCountForVirtualization}px)`
        }

        const parent = wrapper.parentElement
        invariant(parent, 'parent is required')
        const offsetTopUntilParent = getOffsetTop(wrapper, {
          finshWhenReaching: parent,
        })
        parent.style.height = offsetTopUntilParent + tableHeight + 'px'
        parent.style.overflowAnchor = 'none'

        const rowActionsContainer = wrapper.querySelector<HTMLDivElement>(
          '.row-actions-container'
        )

        // add eventlistener to scroller, on each scroll in the Y axis, translate actionsWrapperRef.current up and down
        const handleScroll = debounce(() => {
          if (typeof _stickyWrapperParentOffsetTop === 'undefined') return
          let translateInPx = 0
          if (scroller.scrollTop >= stickyWrapperParentOffsetTop) {
            translateInPx = scroller.scrollTop - stickyWrapperParentOffsetTop
          }

          if (stickyWrapperParentOffsetBottom) {
            const scrollRemaining =
              scroller.scrollHeight -
              translateInPx -
              scroller.clientHeight -
              stickyWrapperParentOffsetTop
            if (scrollRemaining < stickyWrapperParentOffsetBottom) {
              translateInPx =
                scroller.scrollHeight -
                stickyWrapperParentOffsetTop -
                scroller.clientHeight -
                stickyWrapperParentOffsetBottom
            }
          }

          if (rowActionsContainer) {
            rowActionsContainer.style.transform = `translateY(-${Math.floor(
              translateInPx
            )}px)`
          }
          if (rightColumn) {
            rightColumn.style.transform = `translateY(-${Math.floor(
              translateInPx
            )}px)`
          }
        })

        scroller.addEventListener('scroll', handleScroll, { passive: false })

        return () => {
          wrapper.style.removeProperty('height')
          wrapper.style.removeProperty('position')
          wrapper.style.removeProperty('top')
          parent?.style.removeProperty('height')
          scroller?.removeEventListener('scroll', handleScroll)
        }
      }
    }, [
      scrollerRefWasSet,
      stickyWrapperRef,
      tableHeight,
      stickyWrapperParentOffsetTop,
      stickyWrapperParentOffsetBottom,
      rows.length,
      fullView,
      _stickyWrapperParentOffsetTop,
      extraRowCountForVirtualization,
      editorHeight,
    ])

    React.useEffect(() => {
      if (!fullView || !stickyWrapperRef || !treeManager?.getNode) return
      if (typeof document === 'undefined') return

      const documentTitle = document.querySelector<HTMLDivElement>(
        `[data-document-title="${idToSourceBlockId(block.id, {
          getNode: treeManager.getNode,
        })}"]`
      )

      if (!documentTitle) return

      let prevHeight: number | null = null

      const calc = () => {
        const stickyWrapperParent = stickyWrapperRef.current?.parentElement
        invariant(stickyWrapperParent, 'stickyWrapperParent is required')
        if (!stickyWrapperParent) return
        const offsetTop = getOffsetTop(stickyWrapperParent, {
          finshWhenReaching: `div#${tabContentWrapperId}`,
        })
        setStickyWrapperParentOffsetTop(offsetTop)
      }

      const observer = new ResizeObserver((entries) => {
        // we only want to trigger the calc when the height of the document title changes
        for (const entry of entries) {
          // Use entry.target.getBoundingClientRect().top for Y position
          const newHeight = entry.target.getBoundingClientRect().height
          if (prevHeight === null || prevHeight !== newHeight) {
            prevHeight = newHeight
            calc()
          }
        }
      })
      observer.observe(documentTitle)

      calc()

      return () => {
        observer.disconnect()
      }
    }, [
      scrollerRefWasSet,
      block.id,
      fullView,
      stickyWrapperRef,
      treeManager?.getNode,
    ])

    React.useEffect(() => {
      if (!fullView) return
      const filler = document.querySelector<HTMLDivElement>(`#editor-filler`)
      if (!filler) return

      function calc() {
        if (!filler) return
        setFillerHeight(filler.offsetHeight)
      }

      const observer = new ResizeObserver(calc)
      calc()
      observer.observe(filler)

      return () => {
        observer.disconnect()
      }
    }, [fullView, scrollerRefWasSet])

    const contextMenuOn = React.useRef<Item | null>(null)
    const contextMenuRef = React.useRef<HTMLDivElement>(null)

    const handleCellsEdited = React.useCallback(
      (newValues: readonly EditListItem[]): true => {
        if (readonly) return true
        const treeManager = treeManagerRef.current
        if (!treeManager) return true

        const node = treeManager.getNode(block.id)
        if (!node) return true

        const parentBlock = node.value

        const redo: RepoUndoStackItem['redo'] = []
        const undo: RepoUndoStackItem['undo'] = []

        const updates: UpdateBlockParams[] = []
        const rowsToFocus: Item[] = []
        newValues.map(({ location: cell, value: newValue }) => {
          const [colNumber, rowNumber] = cell
          const row = rows[rowNumber]

          if (!row) return

          const editedColumn = columns[colNumber]
          if (!editedColumn) return

          const editedColumnId = editedColumn.id

          if (editedColumnId === '__title') {
            if (newValue.kind !== GridCellKind.Custom) return
            if (row.id === justCreatedTableRowId) {
              setTimeout(
                () =>
                  onFinishedCreatingRow?.(null, {
                    isVisible: true,
                  }),
                300
              )
            }
            const parsedNewValue = blockCellSchema.safeParse(newValue.data)
            if (!parsedNewValue.success) return

            const rowPath = treeManager.getPath(row.id)
            if (!rowPath) return

            rowsToFocus.push(cell)
            updates.push({
              id: row.id,
              instanceChildTarget: row.instanceChildTarget,
              data: getTitleUpdateOverride({
                title: parsedNewValue.data.title ?? row.title,
                block: row,
                selectedVariants: selectedDashboardVariants,
                parent: parentBlock,
              }),
              path: rowPath,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            })
            return
          }

          const { block: cellBlock, blockFromTemplate: cellFromTemplate } =
            getBlockFromColumnId(editedColumnId, {
              row,
              treeManager,
            })

          if (!cellBlock || !cellFromTemplate) return
          const updatedBlock = { ...cellBlock }
          const updatedBlockPath = treeManager.getPath(updatedBlock.id)
          if (!updatedBlockPath) return

          switch (updatedBlock.type) {
            case 'boolean': {
              if (newValue.kind !== GridCellKind.Custom) return
              const parsedNewValue = booleanCellSchema.safeParse(newValue.data)
              if (!parsedNewValue.success) return
              updatedBlock.value = parsedNewValue.data.value
              break
            }
            case 'media': {
              const parsedNewValue = mediaPickerCellSchema.safeParse(
                newValue.data
              )
              if (!parsedNewValue.success) return
              if (
                parsedNewValue.data.kind === 'media-picker-pasted-cell' ||
                (!parsedNewValue.data.event &&
                  'url' in parsedNewValue.data.value)
              ) {
                const currentUrl =
                  updatedBlock.value.status === 'uploaded'
                    ? updatedBlock.value.url
                    : null
                const newUrl =
                  'url' in parsedNewValue.data.value
                    ? parsedNewValue.data.value.url
                    : null
                const isSameUrl = currentUrl === newUrl
                if (isSameUrl) return
                if (!('url' in parsedNewValue.data.value)) return

                rowsToFocus.push(cell)
                return updates.push({
                  id: updatedBlock.id,
                  instanceChildTarget: updatedBlock.instanceChildTarget,
                  data: {
                    ...updatedBlock,
                    value: {
                      ...parsedNewValue.data.value,
                      lastModified: new Date().getTime(),
                      status: 'uploaded',
                    },
                  },
                  strategy: 'merge',
                  path: updatedBlockPath,
                  isoNow: new Date().toISOString(),
                })
              }

              const event = parsedNewValue?.data
                ?.event as React.ChangeEvent<HTMLInputElement>
              const [file] = Array.from(event?.target?.files ?? [])
              if (!file) return
              if (
                !isValidTypeForCategory(
                  file.type,
                  Object.keys(
                    updatedBlock.constraints.allowedMimeTypes ?? {}
                  ) as MediaBlockValueType[]
                )
              ) {
                return
              }
              if (!repoId || !teamId) return

              const redoOp: RepoUndoStackItem['redo'][number] = {
                op: 'skip',
                args: {},
              }
              const undoOp: RepoUndoStackItem['undo'][number] = {
                op: 'skip',
                args: {},
              }
              redo.push(redoOp)
              undo.push(undoOp)

              run(async () => {
                const { uploadURL } = await uploadFileToRepo(file, {
                  teamId,
                  repoId,
                  fileHash: await getFileHashSHA256(file),
                })

                const mediaType = getSafeMediaTypeByConstraints(
                  file.type,
                  updatedBlock.constraints.allowedMimeTypes
                )

                let data
                switch (mediaType) {
                  case 'audio':
                  case 'file':
                    data = {
                      type: updatedBlock.type,
                      value: {
                        type: mediaType,
                        status: 'uploaded' as const,
                        mimeType: file.type,
                        fileName: file.name,
                        fileSize: file.size,
                        url: uploadURL,
                        lastModified: file.lastModified,
                      },
                    }
                    break

                  case 'image':
                    data = await new Promise<AtLeast<MediaBlock, 'type'>>(
                      (resolve) => {
                        const imageElement = new Image()
                        imageElement.onload = () => {
                          resolve({
                            type: updatedBlock.type,
                            value: {
                              type: mediaType,
                              status: 'uploaded',
                              mimeType: file.type,
                              fileName: file.name,
                              fileSize: file.size,
                              url: uploadURL,
                              width: imageElement.width,
                              height: imageElement.height,
                              cloudflareImage: {
                                uploadState: 'will-not-upload',
                                url: null,
                                reason: 'deprecated',
                              },
                              altText: null,
                              lastModified: file.lastModified,
                            },
                          })
                        }
                        imageElement.src = URL.createObjectURL(file)
                      }
                    )
                    break
                  case 'video': {
                    data = await new Promise<AtLeast<MediaBlock, 'type'>>(
                      (resolve) => {
                        const videoElement = document.createElement('video')
                        videoElement.addEventListener('loadedmetadata', () => {
                          resolve({
                            type: updatedBlock.type,
                            value: {
                              type: mediaType,
                              status: 'uploaded',
                              mimeType: file.type,
                              fileName: file.name,
                              fileSize: file.size,
                              width: videoElement.videoWidth,
                              height: videoElement.videoHeight,
                              url: uploadURL,
                              lastModified: file.lastModified,
                            },
                          })

                          videoElement.remove()
                        })
                        const src = URL.createObjectURL(file)
                        videoElement.src = src
                      }
                    )
                    break
                  }
                  default:
                    return toast.error('Unsupported media type')
                }

                const params: UpdateBlockParams = {
                  id: updatedBlock.id,
                  instanceChildTarget: updatedBlock.instanceChildTarget,
                  data,
                  path,
                  strategy: 'merge',
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache?.mutate.updateBlock(params)
                // @ts-ignore
                redoOp.op = 'updateBlock'
                // @ts-ignore
                redoOp.args = params
                // @ts-ignore
                undoOp.op = 'updateBlock'
                // @ts-ignore
                undoOp.args = {
                  // @ts-ignore
                  id: updatedBlock.id,
                  // @ts-ignore
                  data: {
                    type: updatedBlock.type,
                    value: { status: 'idle' },
                  },
                  // @ts-ignore
                  path,
                  // @ts-ignore
                  strategy: 'merge',
                  // @ts-ignore
                  isoNow: new Date().toISOString(),
                }
                undoManager.current?.storage.save()
                return result
              })

              rowsToFocus.push(cell)
              updates.push({
                id: updatedBlock.id,
                instanceChildTarget: updatedBlock.instanceChildTarget,
                data: {
                  ...updatedBlock,
                  value: { status: 'uploading' },
                },
                strategy: 'merge',
                path: updatedBlockPath,
                isoNow: new Date().toISOString(),
              })
              return
            }
            case 'number':
              if (newValue.kind !== GridCellKind.Number) return
              updatedBlock.value = newValue.data ?? null
              break
            case 'date': {
              const parsedNewValue = datePickerCellSchema.safeParse(
                newValue.data
              )
              if (!parsedNewValue.success) return
              updatedBlock.value = parsedNewValue.data.date
              break
            }
            case 'color': {
              const parsedNewValue = colorPickerCellSchema.safeParse(
                newValue.data
              )
              if (!parsedNewValue.success) return
              updatedBlock.value = parsedNewValue.data.value
              break
            }
            case 'icon': {
              const parsedNewValue = svgCellSchema.safeParse(newValue.data)
              if (!parsedNewValue.success) return
              updatedBlock.value = parsedNewValue.data.value
              break
            }
            case 'text':
              if (newValue.kind !== GridCellKind.Text) return
              updatedBlock.value = newValue.data ?? ''
              break
            case 'code-snippet':
              if (newValue.kind !== GridCellKind.Text) return
              updatedBlock.value = {
                ...updatedBlock.value,
                code: newValue.data ?? '',
              }
              break
            case 'select':
            case 'reference': {
              const parsedNewValue = multiSelectCellSchema.safeParse(
                newValue.data
              )
              if (!parsedNewValue.success) return
              if (updatedBlock.type === 'select') {
                updatedBlock.value = parsedNewValue.data.values
                if (updatedBlock.id === cellFromTemplate.id) {
                  const newAcceptedValues = Array.from(
                    new Set([
                      ...updatedBlock.constraints.acceptedValues,
                      ...parsedNewValue.data.values,
                    ])
                  )

                  updatedBlock.constraints = {
                    ...updatedBlock.constraints,
                    acceptedValues: newAcceptedValues,
                  }
                }
              } else {
                updatedBlock.value = {
                  ...updatedBlock.value,
                  targetBlockIds: parsedNewValue.data.values ?? [],
                }
              }

              break
            }
            default:
              return
          }

          rowsToFocus.push(cell)
          updates.push({
            id: updatedBlock.id,
            instanceChildTarget: updatedBlock.instanceChildTarget,
            data: getBlockUpdateOverride({
              block: updatedBlock,
              newValue: updatedBlock.value,
              selectedVariants: selectedDashboardVariants,
            }),
            isoNow: new Date().toISOString(),
            path: updatedBlockPath,
            strategy: 'merge',
          })
          return
        })

        const params: BlockBatchMutationParams = {
          mutations: updates.filter(Boolean).map((update) => {
            return {
              op: 'updateBlock',
              args: update,
            } as const
          }),
        }

        if (!params.mutations.length) return true

        replicache?.mutate.blockBatchMutations(params).then((result) => {
          if (result?.undo) {
            undoManager.current?.push({
              redo: [
                focusOpTable,
                { op: 'blockBatchMutations', args: params },
                ...redo,
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { rowsToFocus },
                  },
                },
              ],
              undo: [
                focusOpTable,
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
                ...undo,
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { rowsToFocus },
                  },
                },
              ],
            })
          }
        })

        return true
      },
      [
        readonly,
        treeManagerRef,
        block.id,
        selectedDashboardVariants,
        replicache?.mutate,
        rows,
        columns,
        justCreatedTableRowId,
        onFinishedCreatingRow,
        repoId,
        teamId,
        path,
        undoManager,
        focusOpTable,
        sourceBlockId,
      ]
    )

    React.useEffect(() => {
      const handleCustomEvent = (e: Event) => {
        if (e instanceof CustomEvent) {
          if (e.detail.refresh) {
            setGridSelection((p) => ({ ...p }))
            return
          }
          const rowsToFocus = e.detail.rowsToFocus as Item[]
          if (!rowsToFocus) return
          const firstRow = rowsToFocus[0]
          const firstRowIndex = firstRow?.[1] ?? 0
          if (!firstRow || firstRowIndex === undefined || firstRowIndex < 0) {
            return
          }
          if (
            !document.activeElement?.closest(
              `[data-data-editor="${sourceBlockId}"]`
            )
          ) {
            dataEditorRef.current?.focus()
          }
          setGridSelection({
            rows:
              rowsToFocus.length > 1
                ? CompactSelection.fromSingleSelection([
                    firstRowIndex,
                    rowsToFocus.length + firstRowIndex,
                  ])
                : CompactSelection.empty(),
            columns: CompactSelection.empty(),
            current:
              rowsToFocus.length > 1
                ? undefined
                : {
                    cell: firstRow,
                    rangeStack: [],
                    range: {
                      x: firstRow[0],
                      y: firstRowIndex,
                      width: 1,
                      height: 1,
                    },
                  },
          })
        }
      }

      window.addEventListener(
        `${sourceBlockId}-ur-custom-event`,
        handleCustomEvent
      )

      return () => {
        window.removeEventListener(
          `${sourceBlockId}-ur-custom-event`,
          handleCustomEvent
        )
      }
    }, [sourceBlockId])

    const lastColumnVisitedBeforeActions = React.useRef(0)

    const focusLastVisitedCellInTable = () => {
      dataEditorRef.current?.focus()
      setGridSelection({
        columns: CompactSelection.empty(),
        rows: CompactSelection.empty(),
        current: {
          cell: [lastColumnVisitedBeforeActions.current, rows.length - 1],
          range: {
            x: lastColumnVisitedBeforeActions.current,
            y: rows.length - 1,
            width: 1,
            height: 1,
          },
          rangeStack: [],
        },
      })
    }

    return (
      <>
        <div className="flex relative h-full min-w-max" ref={tableContainerRef}>
          {!rows.length && emptyState && (
            <div
              style={{
                top: HEADER_HEIGHT_WITH_BORDERS + 'px',
                position: 'absolute',
                zIndex: 50,
                width: '100%',
                height: ROW_HEIGHT + 'px',
              }}
            >
              {emptyState}
            </div>
          )}

          {Boolean(hasRowsSelected && !readonly) && (
            <IconButton
              onClick={() => {
                handleDeleteWithErrorHandling({
                  columns: CompactSelection.empty(),
                  rows: gridSelection.rows,
                })
              }}
              className="absolute right-[calc(100%+12px)] max-w-[52px] w-full py-0.5 rounded-md leading-normal top-1.5 pr-1.5 pl-2 bg-dark-shade hover:bg-dark-shade-hover border border-dark-border-solid text-xs text-dark-muted tabular-nums"
            >
              {/* amount of rows, but if selected is more than 1k, use a shorthand */}
              {Intl.NumberFormat('en-US', {
                notation: 'compact',
                maximumFractionDigits: 1,
              }).format(gridSelection.rows.length)}
              <TrashIcon width={14} height={14} className="shrink-0" />
            </IconButton>
          )}

          <TableContextMenu
            ref={contextMenuRef}
            path={path}
            parentBlock={block}
            renderIf={tabPrefix === ''}
            disabled={!gridSelection.rows.length}
            duplicateCallback={() => {
              const selectedRowIndex = gridSelection.rows.toArray()?.[0]
              if (!selectedRowIndex && selectedRowIndex !== 0) return
              const newRowIndex =
                !fullView && selectedRowIndex === rows.length - 1
                  ? selectedRowIndex
                  : selectedRowIndex + 1
              dataEditorRef.current?.focus()
              setGridSelection({
                rows: CompactSelection.fromSingleSelection(newRowIndex),
                columns: CompactSelection.empty(),
              })
            }}
            selectedRows={
              gridSelection.rows.length > 0
                ? gridSelection.rows
                    .toArray()
                    .map((rowIndex) => rows[rowIndex])
                    .filter(Boolean)
                : []
            }
          >
            <div className="flex h-full">
              <DataEditorWithRowActions
                columnSelect="none"
                updateJustCreateadTableRowsId={updateJustCreateadTableRowsId}
                rowSelectionMode="multi"
                ref={mergeRefs([dataEditorRef, ref])}
                fixedShadowX={false}
                fixedShadowY={false}
                scrollerXRef={
                  bleedContainerRef as React.MutableRefObject<HTMLDivElement>
                }
                scrollerYRef={
                  scrollerRef as React.MutableRefObject<HTMLDivElement>
                }
                tableContainerRef={tableContainerRef}
                scrollOffsetTop={stickyWrapperParentOffsetTop}
                scrollOffsetBottom={stickyWrapperParentOffsetBottom}
                readonly={readonly}
                onRowAppended={
                  handleNewRow
                    ? () => {
                        const promiseOrResult = handleNewRow()
                        if (promiseOrResult instanceof Promise) {
                          return promiseOrResult.then((result) => {
                            dataEditorRef.current?.focus()
                            return result
                          })
                        }

                        dataEditorRef.current?.focus()
                        return promiseOrResult
                      }
                    : undefined
                }
                onCellContextMenu={(cell) => {
                  // select cell if it was not selected
                  const selectedRowIndexes = gridSelection.rows.toArray()
                  if (
                    !gridSelection.rows ||
                    !selectedRowIndexes.includes(cell[1])
                  ) {
                    contextMenuOn.current = cell

                    setGridSelection({
                      columns: CompactSelection.empty(),
                      rows: CompactSelection.fromSingleSelection(cell[1]),
                    })
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && e.metaKey) {
                    handleDeleteWithErrorHandling({
                      columns: gridSelection.columns,
                      rows: gridSelection.rows,
                    })
                    e.stopPropagation()
                    e.preventDefault()
                  }

                  if (!onKeyDown) return

                  const currentColumnIndex =
                    gridSelection.current?.cell[0] ?? -1
                  const currentColumn = columns[currentColumnIndex]

                  if (!currentColumn) return

                  onKeyDown(e, { currentColumn })
                }}
                minColumnWidth={100}
                theme={theme}
                headerIcons={HEADER_ICONS}
                headerHeight={HEADER_HEIGHT}
                rowHeight={ROW_HEIGHT}
                rowActions={rowActions}
                height={tableHeight}
                className="basehub-glide-table"
                getRowThemeOverride={getRowThemeOverrideWrapper}
                customRenderers={RENDERERS as unknown as CustomRenderer[]}
                highlightRegions={highlights}
                getCellContent={getCellContent}
                gridSelection={gridSelection}
                setGridSelection={setGridSelection}
                onGridSelectionChange={(newSelection) => {
                  if (isDiff) {
                    return setGridSelection({
                      columns: CompactSelection.empty(),
                      rows: CompactSelection.empty(),
                    })
                  }

                  if (contextMenuOn.current) {
                    contextMenuOn.current = null
                    return
                  }

                  const newRowIndex = newSelection.current?.cell[1]
                  const isLastRow = newRowIndex === rows.length
                  if (recentlyCreatedRows) return
                  if (isLastRow) {
                    lastColumnVisitedBeforeActions.current =
                      newSelection.current?.cell[0] ?? 0
                    const moreRowsButton =
                      document.querySelector<HTMLSpanElement>(
                        '#load-more-rows-button-' + block.id
                      )
                    if (moreRowsButton) {
                      setGridSelection({
                        columns: CompactSelection.empty(),
                        rows: CompactSelection.empty(),
                      })
                      moreRowsButton.focus()
                    } else {
                      const newRowButton =
                        document.querySelector<HTMLButtonElement>(
                          '#new-row-button-' + block.id
                        )
                      if (newRowButton) {
                        setGridSelection({
                          columns: CompactSelection.empty(),
                          rows: CompactSelection.empty(),
                        })
                        newRowButton.focus()
                      }
                    }
                    return
                  }
                  setGridSelection(newSelection)
                }}
                getCellsForSelection
                onColumnMoved={readonly ? undefined : handleColumnMoved}
                fillHandle
                trapFocus
                allowedFillDirections="vertical"
                columns={columns}
                rows={rows}
                rowMarkers={
                  readonly
                    ? 'number'
                    : {
                        kind: 'both',
                        theme: { borderColor: 'transparent' },
                      }
                }
                smoothScrollX
                smoothScrollY
                // @ts-ignore
                onDelete={handleDeleteWithErrorHandling}
                scaleToRem
                // use this to enable cell editing, instead of the onCellEdited. This one will let us make bulk edits
                onCellsEdited={handleCellsEdited}
                block={block}
                onFinishedEditing={() => {
                  setTimeout(
                    () => onFinishedCreatingRow?.(null, { isVisible: true }),
                    300
                  )
                }}
                path={path}
                justCreatedTableRowId={justCreatedTableRowId}
                onFinishedCreatingRow={onFinishedCreatingRow}
                onCellEdited={async (
                  cell: Item,
                  newValue: EditableGridCell
                ) => {
                  return handleCellsEdited([
                    { location: cell, value: newValue },
                  ])
                }}
                onColumnResizeEnd={isDiff ? undefined : handleColumnResizeEnd}
              />
            </div>
          </TableContextMenu>

          {!readonly && (
            <ConfirmSortDeletionModal
              block={block}
              path={path}
              open={sortRemovalConfirmationModalOpened}
              onOpenChange={setSortRemovalConfirmationModalOpened}
            />
          )}

          <RightColumn
            listLength={rows.length + 1}
            ref={rightColumnRef}
            className="will-change-transform -ml-[7px]"
          >
            {!disableColumnAdd && (
              <AddColumnMenu
                block={block}
                path={templateBlockPath as string}
                className="w-full"
              />
            )}
          </RightColumn>
        </div>

        {!!(Boolean(!readonly && !!handleNewRow) || !allRowsShown) && (
          <div
            className="w-full"
            style={{
              height: ROW_HEIGHT,
              transform: !allRowsShown
                ? 'translateY(-200%)'
                : 'translateY(-100%)',
              position: !allRowsShown ? 'relative' : 'absolute',
            }}
          >
            {!allRowsShown && (
              <LoadMoreItemsButton
                data-block-id={block.id}
                id={'load-more-rows-button-' + block.id}
                block={block}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return

                  e.preventDefault()
                  if (e.key === 'ArrowDown') {
                    const newRowButton =
                      document.querySelector<HTMLButtonElement>(
                        '#new-row-button-' + block.id
                      )
                    if (!newRowButton) return
                    newRowButton.focus()
                  } else if (e.key === 'ArrowUp') {
                    focusLastVisitedCellInTable()
                  }
                }}
                onClick={(e) => {
                  if (e.target !== e.currentTarget) return
                  loadMore()
                }}
              >
                {isLoading ? (
                  <>
                    <Spinner width={16} height={16} className="mr-1.5" />
                    &nbsp;Loading...&nbsp;&nbsp;
                  </>
                ) : (
                  <>
                    <ArrowDownIcon width={16} height={16} className="mr-1.5" />
                    Load more&nbsp;({length - rowsShown} remaining)&nbsp;&nbsp;
                  </>
                )}
                {path && editorMode === 'write:all' && (
                  <ShowOnFirstLoadMenu block={block} path={path} />
                )}{' '}
              </LoadMoreItemsButton>
            )}

            {Boolean(!readonly && !!handleNewRow) && (
              <Button
                id={'new-row-button-' + block.id}
                className="flex w-full z-40 select-none bg-dark-base focus-visible:bg-dark-shade !-outline-offset-1 cursor-default items-center justify-start rounded-none border-b border-dark-border-solid px-2 py-1.5 text-xs text-dark-faint hover:bg-dark-shade"
                data-block-id={block.id}
                style={{ height: ROW_HEIGHT }}
                unstyled
                focusStyled
                iconSide="left"
                icon={<PlusIcon width={16} height={16} className="mr-1.5" />}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    const newRowButton =
                      document.querySelector<HTMLButtonElement>(
                        '#load-more-rows-button-' + block.id
                      )
                    if (newRowButton) {
                      newRowButton.focus()
                    } else {
                      focusLastVisitedCellInTable()
                    }
                  }
                }}
                onClick={() => {
                  const indexOfTitleCell = getRowTitleCandidateIndex(columns)

                  dataEditorRef.current
                    ?.appendRow(indexOfTitleCell, true)
                    .then(() => {
                      setGridSelection({
                        columns: CompactSelection.empty(),
                        rows: CompactSelection.empty(),
                        current: {
                          cell: [indexOfTitleCell, rows.length],
                          range: {
                            x: indexOfTitleCell,
                            y: rows.length,
                            width: 1,
                            height: 1,
                          },
                          rangeStack: [],
                        },
                      })
                    })
                }}
              >
                New Row
              </Button>
            )}
          </div>
        )}
      </>
    )
  }
)

const DataEditorWithRowActions = React.memo(
  React.forwardRef<
    DataEditorRef,
    (Omit<React.ComponentProps<typeof DataEditor>, 'rows'> &
      Omit<TableProps, 'treeManager' | 'emptyState'>) & {
      rows: ListBlockRow[]
      gridSelection: GridSelection
      setGridSelection: React.Dispatch<React.SetStateAction<GridSelection>>
      columns: BaseHubCollectionColumn[]
      readonly: boolean
      tableContainerRef: React.RefObject<null | HTMLDivElement>
      updateJustCreateadTableRowsId: (newRowIds: Block['id'][]) => void
    }
  >(
    (
      {
        columns,
        readonly,
        rows,
        gridSelection,
        setGridSelection,
        updateJustCreateadTableRowsId,
        height,
        tableContainerRef,
        onFinishedCreatingRow,
        justCreatedTableRowId,
        ...props
      },
      ref
    ) => {
      const [hoveredRow, setHoveredRow] = React.useState<number | undefined>()
      const editorHeight = useEditorMainContentHeight()
      const { active } = useDndContext()
      const dataEditorRef = React.useRef<DataEditorRef>(null)

      const onItemHovered = React.useCallback((args: GridMouseEventArgs) => {
        const [_, row] = args.location
        setHoveredRow(args.kind !== 'cell' ? undefined : row)
      }, [])

      const selectedItems = React.useMemo(() => {
        const rowSelection = gridSelection.rows.toArray()
        if (!rowSelection || !rowSelection.length) return []

        return rowSelection.map((r) => rows[r] ?? null).filter(Boolean)
      }, [gridSelection?.rows, rows])

      const onDropFilesCallback = React.useCallback(
        (createdRows: Block[]) => {
          if (!createdRows.length) return

          if (justCreatedTableRowId) {
            onFinishedCreatingRow?.(null, { isVisible: true })
          }

          updateJustCreateadTableRowsId(createdRows.map((r) => r.id))
        },
        [
          justCreatedTableRowId,
          updateJustCreateadTableRowsId,
          onFinishedCreatingRow,
        ]
      )

      const scrollTop = props?.scrollerYRef?.current?.scrollTop ?? 0
      const startPoint = HEADER_HEIGHT_WITH_BORDERS
      const startPointWithOffsetTop = startPoint + (props?.scrollOffsetTop ?? 0)
      const scrollerStart = scrollTop
      const scrollerEnd = scrollTop + (editorHeight ?? 0)
      const [draggedItem] = active?.data.current?.keysDragged ?? []
      const draggedItemId =
        draggedItem?.id ?? active?.id.toString().split('/')[1]

      return (
        <>
          {!readonly && (
            <div className="row-actions-container z-50 will-change-transform">
              <div
                id={`${listBlockDndControllers.dndType}-${props.block.id}`}
                aria-hidden="true"
              />

              {rows.length ? (
                rows.map((row, index) => {
                  const droppableZoneHeight = ROW_HEIGHT / 2

                  const actionsStart =
                    startPointWithOffsetTop + index * ROW_HEIGHT
                  const actionsEnd =
                    startPointWithOffsetTop + index * ROW_HEIGHT + ROW_HEIGHT
                  const isInView =
                    actionsStart >= scrollerStart && actionsEnd <= scrollerEnd

                  if (
                    props?.scrollOffsetTop !== Infinity &&
                    !isInView &&
                    draggedItemId !== idToSourceBlockId(row)
                  ) {
                    return null
                  }

                  return (
                    <div key={row.id}>
                      <React.Suspense fallback={null}>
                        {props.rowActions && (
                          <RowActions
                            index={index}
                            row={row}
                            block={props.block}
                            path={props.path}
                            hovered={hoveredRow === index}
                            selectedItems={selectedItems}
                            onRowCreated={() => {
                              const y = index + 1
                              const indexOfTitleCell =
                                getRowTitleCandidateIndex(columns)

                              // set selection to newly created row cell title
                              setGridSelection({
                                columns: CompactSelection.empty(),
                                rows: CompactSelection.empty(),
                                current: {
                                  cell: [indexOfTitleCell, y],
                                  range: {
                                    x: indexOfTitleCell,
                                    y,
                                    width: 1,
                                    height: 1,
                                  },
                                  rangeStack: [],
                                },
                              })

                              dataEditorRef?.current?.focus()
                            }}
                          />
                        )}
                      </React.Suspense>
                      <DroppableProvider>
                        <DroppableTopZone
                          style={{
                            height: droppableZoneHeight,
                            top: index * ROW_HEIGHT + startPoint,
                            width:
                              (tableContainerRef.current?.clientWidth ?? 0) +
                              48,
                          }}
                          id={row.id}
                          parentBlock={props.block}
                          parentPath={props.path}
                          index={index}
                          onDropFilesCallback={onDropFilesCallback}
                        />

                        <DroppableBottomZone
                          style={{
                            height: droppableZoneHeight,
                            top:
                              index * ROW_HEIGHT +
                              startPoint +
                              droppableZoneHeight,
                            width:
                              (tableContainerRef.current?.clientWidth ?? 0) +
                              48,
                          }}
                          id={row.id}
                          parentBlock={props.block}
                          parentPath={props.path}
                          index={index}
                          onDropFilesCallback={onDropFilesCallback}
                        />
                      </DroppableProvider>
                    </div>
                  )
                })
              ) : (
                <DroppableProvider>
                  <DroppableTopZone
                    style={{
                      height: ROW_HEIGHT,
                      top: ROW_HEIGHT,
                      width: tableContainerRef.current?.clientWidth,
                    }}
                    id={null}
                    parentBlock={props.block}
                    parentPath={props.path}
                    index={0}
                    onDropFilesCallback={onDropFilesCallback}
                  />
                </DroppableProvider>
              )}
            </div>
          )}

          <div
            style={{ height }}
            data-data-editor={idToSourceBlockId(props.block)}
          >
            <DataEditorWithResizableColumns
              // @ts-ignore
              // eslint-disable-next-line react-compiler/react-compiler
              ref={mergeRefs([dataEditorRef, ref])}
              {...props}
              rows={rows.length}
              readonly={readonly}
              columns={columns}
              onItemHovered={onItemHovered}
              gridSelection={gridSelection}
            />
          </div>
        </>
      )
    }
  )
)

const DataEditorWithResizableColumns = React.memo(
  React.forwardRef<
    DataEditorRef,
    React.ComponentProps<typeof DataEditor> & {
      columns: BaseHubCollectionColumn[]
      readonly: boolean
    }
  >(({ columns, readonly, ...props }, ref) => {
    const dataEditorRef = React.useRef<DataEditorRef>(null)
    const [structure, setStructure] =
      React.useState<BaseHubCollectionColumn[]>(columns)

    const handleColumnResize = React.useCallback(
      (column: GridColumn, size: number) => {
        setStructure((prev) => {
          return prev.map((_column) => {
            if (_column.id === column.id) {
              return { ..._column, width: size }
            }

            return _column
          })
        })
      },
      []
    )

    React.useEffect(() => {
      setStructure(columns)
    }, [columns])

    return (
      <DataEditor
        // eslint-disable-next-line react-compiler/react-compiler
        ref={mergeRefs([dataEditorRef, ref])}
        {...props}
        columns={structure}
        onColumnResize={readonly ? undefined : handleColumnResize}
        columnSelect={readonly ? 'none' : 'multi'}
        rowSelect={readonly ? 'none' : 'multi'}
      />
    )
  })
)

export const ListInputTableView = ({
  block,
  path,
  fullView,
  variant,
  listTable,
}: ViewComponentProps) => {
  const [selectedView] = useSelectedView(block)
  const editorMode = useEditorMode()
  const readonly = editorMode === 'read'
  const addTab = useTabsStore((s) => s.addTab)
  const { activeTabKey: blockId } = useDashboardSlugs()
  const justCreatedTableRowId = useEditorStore((s) => s.justCreatedTableRowId)
  const setJustCreatedTableRowId = useEditorStore(
    (s) => s.setJustCreatedTableRowId
  )
  const setFocus = useRepoStore((s) => s.setFocus)
  const stickyWrapperRef = React.useRef<HTMLDivElement>(null)
  const undoManager = useRepoUndoManager()
  const sourceBlockId = idToSourceBlockId(block)

  const replicache = useRepoReplicache().data?.replicache
  const bleedContainerRef = React.useRef<HTMLDivElement>(null)
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const treeManagerRef = useWorkingTreeStore((s) => s.treeRef)
  const tableRef = React.useRef<DataEditorRef>(null)

  const handleNewRow = React.useCallback(async () => {
    invariant(replicache, 'replicache should be defined')
    invariant(block.typeOptions !== null, 'block.typeOptions should be defined')
    const newRowId = getId()
    const params: CreateBlockParams = {
      data: {
        id: newRowId,
        title: '',
        type: blockTypesRecord.instance,
        typeOptions: {
          targetBlockId: block.typeOptions.targetBlockId,
        },
      },
      isoNow: new Date().toISOString(),
      position: { type: 'last' },
      path,
      idSeed: getId(),
      parentInstanceChildTarget: block.instanceChildTarget,
    }
    const { newBlock: newRow, undo } =
      await replicache.mutate.createBlock(params)

    if (!newRow) return
    if (undo) {
      const focusOp = {
        op: 'focus',
        args: {
          focus: {
            type: 'block:wrapper',
            blockId: sourceBlockId,
          },
          preferredTabKey: blockId,
        },
      } satisfies RepoUndoOps[number]
      undoManager.current?.push({
        redo: [focusOp, { op: 'createBlock', args: params }],
        undo: [
          focusOp,
          { op: 'blockBatchMutations', args: { mutations: undo } },
        ],
      })
    }

    setJustCreatedTableRowId(newRow.id)
    return 'bottom' as const
  }, [
    replicache,
    block.typeOptions,
    block.instanceChildTarget,
    path,
    setJustCreatedTableRowId,
    sourceBlockId,
    blockId,
    undoManager,
  ])

  return (
    // relative is super important here to get the proper offset top of the sticky wrapper
    <div
      className={clsx(
        'w-full z-30 relative mt-1',
        variant !== 'default' && 'overflow-x-auto'
      )}
    >
      <div ref={stickyWrapperRef}>
        <FullBleedContainer
          role="list-view-bleed-container"
          disabled={variant !== 'default'}
          ref={bleedContainerRef}
          maxWidth={fullView ? 1969 : 1024}
          flexGrow
          bleedContainerClassName="h-full px-20"
        >
          <Table
            ref={tableRef}
            stickyWrapperRef={stickyWrapperRef}
            {...(!readonly && { handleNewRow })}
            justCreatedTableRowId={justCreatedTableRowId}
            rowActions={variant !== 'nested' && variant !== 'nested-union'}
            tabPrefix=""
            onFinishedCreatingRow={(newRow, { isVisible }) => {
              if (!justCreatedTableRowId) return
              if (!isVisible) {
                if (!treeManager || !newRow) return

                const activeDocument = blockId
                  ? treeManager.getNode(blockId, {
                      fallbackKey: 'sourceBlockId',
                    })?.value
                  : null

                setFocus({
                  type: 'block:document-title',
                  blockId: idToSourceBlockId(newRow),
                  temporaryBackLink: {
                    title: activeDocument?.title ?? (block.title || 'Untitled'),
                    id: idToSourceBlockId(activeDocument ?? block),
                    edgeId: activeDocument
                      ? idToSourceBlockId(block)
                      : undefined,
                  },
                })
                addTab({
                  key: justCreatedTableRowId,
                  partiallyOpen: true,
                })
              }
              setJustCreatedTableRowId(null)
            }}
            block={block}
            path={path}
            fullView={fullView}
            editorMode={editorMode}
            listTable={listTable}
            treeManager={treeManager}
            treeManagerRef={treeManagerRef}
            onKeyDown={(e, { currentColumn }) => {
              const bleedContainer = bleedContainerRef.current
              if (!bleedContainer) return
              const verticalScroller = getVerticalScrollerElement()
              if (!verticalScroller) return

              switch (e.key) {
                case 'ArrowRight': {
                  if (e.metaKey) {
                    bleedContainer.scrollLeft = bleedContainer.scrollWidth
                  } else bleedContainer.scrollLeft += currentColumn.width

                  break
                }
                case 'ArrowLeft': {
                  if (e.metaKey) bleedContainer.scrollLeft = 0
                  else bleedContainer.scrollLeft -= currentColumn.width

                  break
                }
                case 'ArrowDown': {
                  if (e.shiftKey || fullView) return
                  if (e.metaKey) {
                    verticalScroller.scrollTop = verticalScroller.scrollHeight
                  } else verticalScroller.scrollTop += ROW_HEIGHT

                  break
                }
                case 'ArrowUp': {
                  if (e.shiftKey || fullView) return
                  if (e.metaKey) {
                    verticalScroller.scrollTop = 0
                  } else verticalScroller.scrollTop -= ROW_HEIGHT

                  break
                }
              }
            }}
            emptyState={
              listTable.isLoading ? (
                <p
                  style={{ height: ROW_HEIGHT }}
                  className="text-xs text-dark-muted flex items-center rounded-none pl-2 font-medium w-full !cursor-auto text-left h-9 bg-dark-base border-b border-dark-border-solid"
                >
                  Loading...
                </p>
              ) : readonly ? (
                <p
                  style={{ height: ROW_HEIGHT }}
                  className="text-xs text-dark-muted flex items-center rounded-none pl-2 font-medium w-full !cursor-auto text-left h-9 bg-dark-base border-b border-dark-border-solid"
                >
                  {selectedView?.filter.length
                    ? 'No rows matching the current filters.'
                    : 'No rows.'}
                </p>
              ) : (
                <Button
                  style={{ height: ROW_HEIGHT }}
                  unstyled
                  focusStyled
                  onClick={() => tableRef.current?.appendRow(0, true)}
                  className="text-xs text-dark-muted rounded-none pl-2 font-medium hover:bg-dark-shade w-full cursor-default left-0 text-left h-9 bg-dark-base border-b border-dark-border-solid"
                >
                  {selectedView?.filter.length
                    ? 'No rows matching the current filters. Click to create one.'
                    : 'No rows. Click to create one.'}
                </Button>
              )
            }
          />
        </FullBleedContainer>
      </div>
    </div>
  )
}

export const ListInputTableViewDiff = ({
  status,
  block,
  fullView,
  diff,
  path,
  treeManager,
  condition,
}: {
  status: BlockChange['status']
  block: ListBlock
  fullView: boolean
  diff: Array<{ id: InstanceBlock['id']; status: BlockChangeChildStatus }>
  path: string
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
  condition: 'added' | 'removed'
}) => {
  const bleedContainerRef = React.useRef<HTMLDivElement>(null)
  const editorWidth = useEditorWidth()
  const DIFF_MIDDLE_GAP = 32
  const stickyWrapperRef = React.useRef<HTMLDivElement>(null)
  const treeManagerRef = React.useRef<typeof treeManager>(treeManager)
  const tabPrefix = useGetTabPrefix()

  const { width, padding } = React.useMemo(() => {
    /**
     * This & the padding both are variable, we should get
     * them from the TabContentDefaultWrapper soon enough
     *  */

    const BLOCK_HORIZONTAL_PADDING = 72
    const DIFF_MIDDLE_BORDER = 1

    const tabContent = document?.querySelector<HTMLDivElement>(
      `#${tabContentId}`
    )
    if (!editorWidth || !tabContent) {
      const width = `calc(100% + ${BLOCK_HORIZONTAL_PADDING}px + ${DIFF_MIDDLE_GAP}px - ${DIFF_MIDDLE_BORDER}px)`
      return { width, padding: BLOCK_HORIZONTAL_PADDING }
    }

    const tabContentWidth = tabContent.getBoundingClientRect().width
    const width = editorWidth / 2 - DIFF_MIDDLE_BORDER
    const BLOCK_MAX_WIDTH =
      tabContentWidth / 2 - BLOCK_HORIZONTAL_PADDING - DIFF_MIDDLE_GAP

    return {
      width: Math.round(width),
      padding: Math.round(width - BLOCK_MAX_WIDTH - DIFF_MIDDLE_GAP),
    }
  }, [editorWidth])

  const listTable = useListTable({
    block,
    treeManager,
    bypassFilterForRowIds: 'all',
  })

  return (
    <div ref={stickyWrapperRef}>
      <div
        className={clsx(
          'no-scrollbar relative left-0 overflow-x-auto overflow-y-hidden',
          condition === 'added' && status === 'deleted' && 'opacity-30'
        )}
        style={{
          width,
          paddingLeft: condition === 'added' ? DIFF_MIDDLE_GAP : padding,
          paddingRight: condition === 'removed' ? DIFF_MIDDLE_GAP : padding,
          transform: `translateX(-${
            condition === 'added' ? DIFF_MIDDLE_GAP : padding
          }px)`,
        }}
      >
        <div
          ref={bleedContainerRef}
          className="relative overflow-hidden min-w-max"
        >
          <Table
            block={block}
            path={path}
            fullView={fullView}
            stickyWrapperRef={stickyWrapperRef}
            editorMode="read"
            listTable={listTable}
            treeManager={treeManager}
            treeManagerRef={treeManagerRef}
            tabPrefix={tabPrefix}
            isDiff
            getRowThemeOverride={(row) => {
              const match = (diff ?? []).find((diffItem) => {
                if (condition === 'removed') {
                  return diffItem.id === row.id
                }

                return diffItem.id === idToSourceBlockId(row)
              })
              if (!match) return undefined

              if (
                match.status === 'reordered-source' ||
                match.status === 'reordered'
              ) {
                return { bgCell: '#5C62F51A', bgCellMedium: '#5C62F51A' }
              }

              switch (match.status) {
                case 'created':
                  return { bgCell: '#46A75833', bgCellMedium: '#46A75833' }
                case 'deleted':
                  return { bgCell: '#71393233', bgCellMedium: '#71393233' }
                case 'modified': {
                  if (condition === 'removed') return undefined
                  return { bgCell: '#F6CA471A', bgCellMedium: '#F6CA471A' }
                }
                default:
                  return undefined
              }
            }}
            emptyState={
              <p
                style={{ height: ROW_HEIGHT }}
                className="text-xs text-dark-muted absolute flex items-center rounded-none pl-2 font-medium w-full cursor-default left-0 text-left bg-dark-base border-b border-dark-border-solid"
              >
                No rows.
              </p>
            }
          />
        </div>
      </div>
    </div>
  )
}

// utils
const getVerticalScrollerElement = () => {
  const tabContentWrapper = document.querySelector<HTMLDivElement>(
    `#${tabContentWrapperId}`
  )
  if (!tabContentWrapper) return null
  const scroller = tabContentWrapper.childNodes[1] as HTMLDivElement

  if (!scroller) return null

  return scroller
}

const getRowTitleCandidateIndex = (columns: BaseHubCollectionColumn[]) => {
  let indexOfTitleCell = -1

  columns.forEach((column, index) => {
    if (column.id === '__title') indexOfTitleCell = index
    else if (column.type === 'text' && indexOfTitleCell === -1) {
      indexOfTitleCell = index
    } else if (column.type === 'number' && indexOfTitleCell === -1) {
      indexOfTitleCell = index
    }
  })
  if (indexOfTitleCell === -1) indexOfTitleCell = 0
  return indexOfTitleCell
}
